import { Link } from '../../../../widgets/buttons';
import { MessageDialog, useDialogManager } from '../../../../widgets/dialog';
import { PageHeaderContainer } from '../../../../widgets/pageHeader';
import { SelectionType, SelectionTypeContainer } from '../../../../widgets/selectionType';
import { VerticalLayout } from '../../../../widgets/layouts';
import React, { FunctionComponent, useEffect } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { useNavigate } from 'react-router-dom';
import { Section } from 'src/components/widgets/section';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { useUpgradeDialogs } from 'src/shared/dialogs';
import { T, backofficeUrl, stringFormat } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import NoImage from 'src/shared/svg/noImage';
import CustomCatLogo from 'src/shared/svg/podCustomCatLogo';
import InvitionLogo from 'src/shared/svg/podInvitionLogo';
import PrintfulLogo from 'src/shared/svg/podPrintfulLogo';
import { providerAction, setHeaderDescriptionCustomComponent, useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';
import { ProductProvider, providerSelectionQuery } from './__generated__/providerSelectionQuery.graphql';

const icons: Record<ProductProvider, JSX.Element> = {
    UNDEFINED: <NoImage />,
    ZAKEKE: <NoImage />,
    CUSTOM_CAT: <CustomCatLogo />,
    INVITION: <InvitionLogo />,
    PRINTFUL: <PrintfulLogo />,
    PRINTFUL_DIRECT: <PrintfulLogo />,
    HP: <NoImage />,
    VERSORI: <NoImage />,
    CASA_DE_LAS_CARCASAS: <NoImage />,
    FAVORITE_GIFT: <NoImage />
};

const providersIds: Record<ProductProvider, number> = {
    UNDEFINED: -1,
    ZAKEKE: 0,
    PRINTFUL: 1,
    CUSTOM_CAT: 2,
    INVITION: 3,
    PRINTFUL_DIRECT: 4,
    HP: 5,
    VERSORI: 6,
    CASA_DE_LAS_CARCASAS: 7,
    FAVORITE_GIFT: 8
};

const providersTooltipText: Record<ProductProvider, string> = {
    UNDEFINED: '',
    ZAKEKE: '',
    PRINTFUL:
        'Printful is one of the largest print-on-demand drop shipping services in the world. Choose from more than 250 products, from clothing and accessories to home decor.',
    CUSTOM_CAT: 'Very popular print on demand company for online merchants. Catalog includes clothing, bags, hats, accessories, jewelry, and drinkware.',
    INVITION: 'Print-on-demand drop shipping company specialized in phone cases with a catalog of over 450 products for more than 100 phone models.',
    PRINTFUL_DIRECT: '',
    HP: '',
    VERSORI: '',
    CASA_DE_LAS_CARCASAS: '',
    FAVORITE_GIFT: ''
};

const ProviderSelection: FunctionComponent<{}> = () => {
    const { showDialog } = useDialogManager();
    const { dispatch, state } = useProductConfigurationState();
    const changeStep = useProductConfigurationChangeStep();
    const { showPlanUpgradeDialog } = useUpgradeDialogs(); // Use the custom hook
    const { sellerMarketplace } = useSubscriptionData();

    const navigate = useNavigate();

    const query = useLazyLoadQuery<providerSelectionQuery>(
        graphql`
            query providerSelectionQuery($salesChannelID: ID!) {
                supportedProviders(salesChannelID: $salesChannelID)
                hasPrintfulKey: hasValidProviderKey(provider: PRINTFUL)
                hasInvitionKey: hasValidProviderKey(provider: INVITION)
                hasCustomCatKey: hasValidProviderKey(provider: CUSTOM_CAT)
                subscription {
                    id
                    maxProducts {
                        pricingVersion
                        canAddProduct
                        maxProducts
                        numProducts
                        numPublishedProducts
                    }
                }
            }
        `,
        {
            salesChannelID: state.salesChannelID
        }
    );

    const availableKeys: Record<ProductProvider, boolean> = {
        UNDEFINED: false,
        ZAKEKE: false,
        PRINTFUL: query.hasPrintfulKey,
        CUSTOM_CAT: query.hasCustomCatKey,
        INVITION: query.hasInvitionKey,
        PRINTFUL_DIRECT: false,
        HP: false,
        VERSORI: false,
        CASA_DE_LAS_CARCASAS: false,
        FAVORITE_GIFT: false
    };

    const handleProviderClick = (provider: ProductProvider) => {
        if (query.subscription?.maxProducts.pricingVersion === 'VERSION_2') {
            const numCurrentPublishedProducts = query.subscription?.maxProducts.numPublishedProducts ?? 0;
            const numMaxPublishedProducts = query.subscription?.maxProducts.maxProducts ?? 0;
            if (numCurrentPublishedProducts >= numMaxPublishedProducts) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        marketPlace={sellerMarketplace ?? undefined}
                        message={
                            <>
                                {stringFormat(
                                    T._(
                                        `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                        'Admin'
                                    ),
                                    numMaxPublishedProducts.toString()
                                )}
                            </>
                        }
                        onUpgradeCompleted={async () => {
                            console.log('Upgrade completato, aggiungere callback caso providerSelection');
                        }}
                    />
                );
                return;
            }
        }

        if (availableKeys[provider] === true) {
            dispatch(providerAction(provider));
            changeStep('import-catalog');
        } else {
            showDialog(
                'no-api-key',
                <MessageDialog
                    message={T._('Supplier not configured.', 'Admin')}
                    buttons={[
                        {
                            label: T._('Go to configuration page', 'Admin'),
                            onClick: () => navigate(backofficeUrl('/integration/provider-configuration-page/' + providersIds[provider]))
                        }
                    ]}
                />
            );
        }
    };

    // Page title
    useEffect(() => {
        dispatch(
            setHeaderDescriptionCustomComponent(
                <PageHeaderContainer.TitleDescription>
                    <PageHeaderContainer.Title>{T._('Print on demand', 'Admin')}</PageHeaderContainer.Title>
                </PageHeaderContainer.TitleDescription>
            )
        );

        return () => {
            dispatch(setHeaderDescriptionCustomComponent(null));
        };
    }, []);

    return (
        <>
            <VerticalLayout>
                <Section title={T._('Choose supplier', 'Admin')}>
                    <SelectionTypeContainer>
                        {query.supportedProviders
                            .filter((x) => x)
                            .map((x) => (
                                <SelectionType
                                    key={x}
                                    tip={<>{T._(providersTooltipText[x], 'Admin')}</>}
                                    label=''
                                    icon={icons[x]}
                                    onClick={() => handleProviderClick(x)}
                                    isPod
                                />
                            ))}
                    </SelectionTypeContainer>
                    <Section
                        title={T._("Don't you see your favorite print-on-demand service?", 'Admin')}
                        description={
                            <Link href='https://zakeke.zendesk.com/hc/en-us/articles/360019068580-Not-integrated-services'>
                                {T._('You can still use it read here how to do it.', 'Admin')}
                            </Link>
                        }
                    />
                </Section>
            </VerticalLayout>
        </>
    );
};

export default ProviderSelection;
